import React from 'react';
import { cn } from '@/lib/utils';

export type MetricStatus = 'ok' | 'warning' | 'critical' | 'neutral' | 'unavailable';

export interface MetricCardProps {
  label: string;
  value: string | number | null;
  /** Optional sub-label shown under the value */
  detail?: string;
  status?: MetricStatus;
  /** Navigate on click */
  onClick?: () => void;
}

const STATUS_RING: Record<MetricStatus, string> = {
  ok: 'border-l-emerald-500',
  warning: 'border-l-amber-500',
  critical: 'border-l-red-500',
  neutral: 'border-l-blue-500',
  unavailable: 'border-l-border',
};

/**
 * Compact KPI card for the Analytics Overview cockpit.
 * Shows a single metric with a coloured left accent, label, value, and optional detail.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  detail,
  status = 'neutral',
  onClick,
}) => {
  const isUnavailable = value === null || value === undefined || status === 'unavailable';

  return (
    <div
      className={cn(
        'border rounded-lg bg-card p-3 border-l-4 flex flex-col justify-between min-w-0',
        STATUS_RING[status],
        onClick && 'cursor-pointer hover:bg-muted/50 transition-colors',
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <span className="text-xs text-muted-foreground truncate">{label}</span>
      <span className={cn(
        'text-lg font-semibold mt-1 truncate',
        isUnavailable ? 'text-muted-foreground' : 'text-foreground',
      )}>
        {isUnavailable ? 'N/A' : value}
      </span>
      {detail && (
        <span className="text-xs text-muted-foreground truncate mt-0.5">{detail}</span>
      )}
    </div>
  );
};
